// Tour step configuration for the new-user guided tours. Targets reference the matching `data-tour="..."` anchor in the DOM.

export type TourId = "chat" | "group" | "asset";

// How a step advances: "manual" = a Next/Got it button; "click" = clicking the spotlighted target; "send" = the page ends the tour when the first message is sent.
export type AdvanceTrigger = "manual" | "click" | "send";

export interface TourStep {
  /** Value of the data-tour anchor to spotlight. */
  target: string;
  title: string;
  body: string;
  placement?: "top" | "bottom" | "left" | "right";
  /** Defaults to "manual". */
  advanceOn?: AdvanceTrigger;
  /** Tappable starter prompt shown in the coachmark — fills the chat input so the user doesn't type. */
  suggestion?: string;
  /** If the target is missing/hidden, skip this step instead of ending the tour. */
  optional?: boolean;
  desktopOnly?: boolean;
  mobileOnly?: boolean;
  requiresProcessedModel?: boolean;
}

export interface TourContext {
  isMobile?: boolean;
  hasProcessedModel?: boolean;
}

// Chat tour (desktop): nav AI icon → chat input. The input step ends on first send.
const CHAT_TOUR: TourStep[] = [
  {
    target: "nav-ai-analyst",
    title: "Meet your AI Analyst",
    body: "This is the AI Analyst — your expert co-pilot for weather, water, and energy decisions. Click it to open the chat.",
    placement: "right",
    advanceOn: "click",
    optional: true, // hidden when the nav link is gated or on mobile — fall back to the chat input step
  },
  {
    target: "chat-input",
    title: "Ask anything to get started",
    body: "No need to type — tap the question below to drop it in, then hit Send and I'll take it from here.",
    placement: "top",
    advanceOn: "send",
    suggestion: "What can Erode help me with?",
  },
];

// Chat tour (mobile): first-time users already land on the chat, so skip the desktop sidebar walk-through (it's replaced by the hamburger menu here) and start right on the chat input — the one action that matters.
const CHAT_TOUR_MOBILE: TourStep[] = [
  {
    target: "chat-input",
    title: "Ask anything to get started",
    body: "No need to type — tap the question below to drop it in, then hit Send and I'll take it from here.",
    placement: "top",
    advanceOn: "send",
    suggestion: "What can Erode help me with?",
  },
];

// First-visit Group page guide.
const GROUP_TOUR: TourStep[] = [
  {
    target: "group-view-toggle",
    title: "Switch how you view this group",
    body: "Flip between a list, an interactive map, and dashboard views of every asset in this group.",
    placement: "bottom",
  },
  {
    target: "group-add-asset",
    title: "Add an asset",
    body: "Add a new asset to this group — a turbine, station, reservoir, or any location you want to track.",
    placement: "bottom",
    optional: true,
  },
  {
    target: "group-asset-table",
    title: "Your assets at a glance",
    body: "Each asset in this group lives here. Select one to open its forecasts, data, and alerts.",
    placement: "top",
    optional: true,
  },
];

// First-visit Asset (Dashboard) page guide.
const ASSET_TOUR: TourStep[] = [
  {
    target: "asset-description",
    title: "Your asset overview",
    body: "The key details and location for this asset live up here.",
    placement: "bottom",
  },
  {
    target: "asset-graph",
    title: "Forecasts & live data",
    body: "Explore weather, water, and energy forecasts plus historical data for this asset right here.",
    placement: "top",
  },
  {
    target: "asset-view",
    title: "Choose how to view it",
    body: "Switch between a graph, a table, and a map view of this asset's data.",
    placement: "bottom",
    desktopOnly: true, // these toggles live in the desktop header only — skip on mobile
    optional: true, // also absent when map view is disabled
  },
  {
    target: "asset-forecast-desktop",
    title: "Pick a forecast",
    body: "Choose which forecast to explore — temperature, wind, solar, precipitation, and more.",
    placement: "bottom",
    desktopOnly: true,
    optional: true,
  },
  {
    target: "asset-forecast-mobile",
    title: "Pick a forecast",
    body: "Choose which forecast to explore — temperature, wind, solar, precipitation, and more.",
    placement: "bottom",
    mobileOnly: true,
    optional: true,
  },
  {
    target: "asset-variation-desktop",
    title: "Switch the variation",
    body: "Toggle the variation of the selected forecast, like hourly values, speed, or power.",
    placement: "bottom",
    desktopOnly: true,
    optional: true, // some forecasts have no variations
  },
  {
    target: "asset-variation-mobile",
    title: "Switch the variation",
    body: "Toggle the variation of the selected forecast, like hourly values, speed, or power.",
    placement: "bottom",
    mobileOnly: true,
    optional: true, // some forecasts have no variations
  },
  {
    target: "asset-timerange-desktop",
    title: "Pick a time range",
    body: "Narrow the chart to a window that matters — the next few days, this week, or the full range.",
    placement: "bottom",
    desktopOnly: true,
    optional: true, // only present once the graph registers its controls
  },
  {
    target: "asset-timerange-mobile",
    title: "Pick a time range",
    body: "Narrow the chart to a window that matters — the next few days, this week, or the full range.",
    placement: "bottom",
    mobileOnly: true,
    optional: true, // only present once the graph registers its controls
  },
  {
    target: "asset-datasets-desktop",
    title: "Show or hide datasets",
    body: "Pick which datasets appear on the chart so you can focus on what matters.",
    placement: "bottom",
    desktopOnly: true,
    optional: true, // only present once the graph registers its controls
  },
  {
    target: "asset-datasets-mobile",
    title: "Show or hide datasets",
    body: "Pick which datasets appear on the chart so you can focus on what matters.",
    placement: "bottom",
    mobileOnly: true,
    optional: true, // only present once the graph registers its controls
  },
  {
    target: "asset-download",
    title: "Download the data",
    body: "Export the data you're viewing as a CSV to use in your own tools.",
    placement: "bottom",
    optional: true, // hidden below the md breakpoint
  },
  {
    target: "asset-list",
    title: "Jump between assets",
    body: "Switch to another asset in this group without leaving the page.",
    placement: "right", // the opener sits at the leading edge of the graph's control row
    optional: true,
  },
  {
    target: "asset-results",
    title: "See your model results",
    body: "When a processed model is ready for this asset, open its results here. Click it to take a look.",
    placement: "bottom",
    advanceOn: "click", // clicking opens the results modal and advances to the modal step
    requiresProcessedModel: true, // only rendered when the asset has a processed model
    optional: true,
  },
  {
    target: "asset-results-modal",
    title: "Explore & export results",
    body: "Pick a model run and time, view the output, copy its API access key, or download the data.",
    placement: "left",
    requiresProcessedModel: true, // unreachable without a model — drop it with the step above
    optional: true, // grace period while the results modal animates open
  },
];

export const TOURS: Record<TourId, TourStep[]> = {
  chat: CHAT_TOUR,
  group: GROUP_TOUR,
  asset: ASSET_TOUR,
};


export const getTourSteps = (tourId: TourId, ctx: TourContext = {}): TourStep[] => {
  const base = tourId === "chat" ? (ctx.isMobile ? CHAT_TOUR_MOBILE : CHAT_TOUR) : TOURS[tourId];
  return base.filter((step) => {
    if (ctx.isMobile && step.desktopOnly) return false;
    if (!ctx.isMobile && step.mobileOnly) return false;
    if (step.requiresProcessedModel && ctx.hasProcessedModel === false) return false;
    return true;
  });
};
